<template>
  <v-col class="register-member-aussie-times-container" lg="6" md="7" sm="12">
    <v-overlay :value="isProcessing">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="70"
        :width="7"
      />
    </v-overlay>
    <v-alert
      width="100%"
      v-if="!!errorMessage"
      dismissible
      type="error"
    >{{ errorMessage }}</v-alert>
    <v-dialog v-model="showSuccess" width="500">
      <v-card class="member-register-modal">
        <v-card-title>
          <div />
          <h2>Renewal Confirmed</h2>
          <div />
        </v-card-title>
        <p>Congratulations! Your renewal is complete.</p>
        <v-btn
          dark
          large
          color="primary"
          v-on:click="returnHome()"
        >RETURN HOME</v-btn>
      </v-card>
    </v-dialog>
    <div class="top-icons" v-if="!membership.isAussieRenewal">
      <a
        class="back-icon"
        v-on:click="goBack"
      >
        <font-awesome-icon icon="long-arrow-alt-left" size="lg" /> Back
      </a>
    </div>
    <h1 class="register-title">Aussie Times {{membership.isAussieRenewal ? 'Renewal' : 'Registration/Renewal'}}</h1>
    <p v-if="!membership.isAussieRenewal">
      Based on your membership selection you're eligible for one year free of Aussie Times!
    </p>
    <div class="subscription-container" v-if="!membership.isAussieRenewal">
      <h4>Would you like to add a 1-year subscription to Aussie Times?</h4>
      <v-radio-group v-model="subscribe">
        <v-radio
          :label="`I would like to receive the Aussie Times in print (1-Year Subscription: ${aussieTimesDisplayPrice})`"
          :value="true"
        ></v-radio>
        <v-radio
          label="I do not wish to receive the Aussie Times in print"
          :value="false"
        ></v-radio>
      </v-radio-group>
    </div>
    <div class="address-container" v-if="subscribe">
      <h4>MAILING ADDRESS</h4>
      <v-alert v-if="subscribe" type="info">Your subscription will be delivered to the address associated with your membership.<br/>Please review your mailing address for accuracy.</v-alert>
      <v-form ref="addressForm" v-model="isValidAddress" class="register-form">
        <CountryDropdown
          :selectedCountry="country"
          v-on:update="updateCountry"
        />
        <StateDropdown
          v-if="country === country_USA"
          required
          :selectedState="stateProvince"
          v-on:update="stateProvince = $event"
        />
        <v-row>
          <v-col
            :lg="country && country !== country_USA ? 8 : 12"
            :md="country && country !== country_USA ? 8 : 12"
            :sm="country && country !== country_USA ? 6 : 12"
          >
            <v-text-field
              outlined
              v-model="address"
              label="Address"
              :rules="[(v) => !!v || 'Address is required']"
            />
          </v-col>
          <v-col
            :lg="country && country !== country_USA ? 4 : 6"
            :md="country && country !== country_USA ? 4 : 6"
            sm="6">
            <v-text-field
              outlined
              v-model="city"
              label="City"
              :rules="[validateCity]"
            />
          </v-col>
          <v-col v-if="country && country !== country_USA" lg="8" md="8" sm="6" >
            <v-text-field
              outlined
              v-model="international"
              label="International Area"
              :rules="[(v) => !!v || 'International Area is required']"
            />
          </v-col>
          <v-col
            :lg="country && country !== country_USA ? 4 : 6"
            :md="country && country !== country_USA ? 4 : 6"
            sm="6">
            <v-text-field
              outlined
              v-model="zipPostal"
              label="Zip/Postal"
              :rules="[validateZip]"
            />
          </v-col>
        </v-row>
      </v-form>
    </div>
    <div class="postage-container" v-if="subscribe">
      <h4>POSTAGE OPTIONS</h4>
      <v-radio-group :disabled="!subscribe" v-model="firstClass">
        <v-radio
          :label="standardText()"
          :value="false"
        ></v-radio>
        <v-radio
          :label="firstClassText()"
          :value="true"
        ></v-radio>
      </v-radio-group>      
      <p v-if="!membership.isAussieRenewal"><strong>All prices shown are in USD.</strong></p>
      <div v-else class="total-container">
        <div class="total">
            <p>TOTAL:</p>
            <p>${{aussieTimesPrice + (firstClass ? firstClassPrice : standardPrice)}}</p>
        </div>
        <p><strong>All prices shown are in USD.</strong></p>
      </div>
    </div>
    <hosted-registration-payment
      v-if="membership.isAussieRenewal"
      class="align-self-center"
      :disabled="!isValidAddress || !plan"
      buttonText="SUBMIT"
      v-on:updatePaymentInfo="pay"
    />
    <v-btn
      v-else
      :dark="isValidAddress"
      large
      color="primary"
      v-on:click="next"
      :disabled="!isValidAddress"
    >NEXT</v-btn>
    <div class="footer-container">
      <v-img height="2rem" contain :src="logoImage" />
      <footer-text />
    </div>
  </v-col>
</template>

<script lang="ts">
import Vue from 'vue';

import CountryDropdown from '@/components/common/inputs/CountryDropdown.vue';
import StateDropdown from '@/components/common/inputs/StateDropdown.vue';
import FooterText from '@/components/common/FooterText.vue';
import HostedRegistrationPayment from '@/components/common/HostedRegistrationPayment.vue';

import userService from '@/services/portal/userService';
import memberService from '@/services/portal/memberService';
import normalizeAddressRegion from '@/helpers/normalize-address-region';
import AuthorizeNetResponseData from '@/types/AuthorizeNetResponseData';
import { Countries } from "@/constants";

const logoImage = require('@/assets/logo.svg');

export default Vue.extend({
  components: { FooterText, CountryDropdown, StateDropdown, HostedRegistrationPayment },
  name: 'AussieTimeRegistrationRenewal',
  data() {
    return {
      logoImage,
      isProcessing: false,
      errorMessage: '',
      showSuccess: false,

      country_USA: Countries.USA,    
      country_Canada: Countries.Canada,

      country: Countries.USA,
      address: "",
      international: "",
      city: "",
      stateProvince: "",
      zipPostal: "",
      aussieTimesDisplayPrice: "",
      isValidAddress: false,
      isValidCard: false,
      subscribe: true,
      firstClass: false,
    }
  },
  watch: {
    membership: function(newValue) {
      if (newValue && newValue.plan) {
        const plan = newValue.plan;

        let aussieTimesDisplayPrice = "";

        if (!!plan.aussieTimesDisplayPrice) {
          aussieTimesDisplayPrice = `${plan.aussieTimesDisplayPrice}`;
        }

        this.aussieTimesDisplayPrice = aussieTimesDisplayPrice;
      }
    },
    primary: function(newValue) {
      if (newValue) {
        this.country = newValue.country;
        this.address = newValue.address;
        this.international = newValue.international;
        this.city = newValue.city;
        this.stateProvince = newValue.stateProvince;
        this.zipPostal = newValue.zipPostal;
        this.normalizeAddressRegion();
      }
    }
  },
  computed: {
    membership() {
      return this.$store.state.registerRenewMembershipInfo;
    },
    primary() {
      return this.$store.state.registerRenewMembershipInfo.primary;
    },
    plan() {
      return this.$store.state.registerRenewMembershipInfo.plan;
    },
    aussieTimesPrice() {
      const plan = this.$store.state.registerRenewMembershipInfo.plan;
      return plan ? plan.aussieTimesPrice : 0;
    },
    standardPrice() {
      const aussieTimeFees = this.$store.state.registerRenewMembershipInfo.aussieTimeFees;
      if (!!aussieTimeFees && this.country === Countries.Canada) {
        return aussieTimeFees.surfacePostageCanada;
      } else if (!!aussieTimeFees && this.country !== Countries.USA) {
        return aussieTimeFees.surfacePostageOther;
      }

      return 0;
    },
    firstClassPrice() {
      const aussieTimeFees = this.$store.state.registerRenewMembershipInfo.aussieTimeFees;
      let price = 25;

      if (!!aussieTimeFees && this.country === Countries.USA) {
        return aussieTimeFees.firstClassUsa;
      } else if(!!aussieTimeFees &&  this.country === Countries.Canada) {
        return aussieTimeFees.firstClassCanada;
      } else if (!!aussieTimeFees) {
        return aussieTimeFees.firstClassOther;
      }

      return price;
    },
  },
  methods: {
    standardText() {
      const aussiePrice = `1-Year Subscription to Aussie Times ${!!this.aussieTimesPrice ? `($${this.aussieTimesPrice}/yr)` : ''}`;
      const standardPostage = `Standard Postage ${!!this.standardPrice ? `($${this.standardPrice})` : ''}`;

      return `${aussiePrice} + ${standardPostage}`;
    },
    firstClassText() {
      const aussiePrice = `1-Year Subscription to Aussie Times ${!!this.aussieTimesPrice ? `($${this.aussieTimesPrice}/yr)` : ''}`;
      const firstClassPostage = `First Class Postage ($${this.firstClassPrice})`;

      return `${aussiePrice} + ${firstClassPostage}`;
    },
    validateCity(v: string) {
      return this.country === Countries.USA
        ? !!v || 'City is required'
        : true
    },
    validateZip(v: string) {
      return this.country === Countries.USA
        ? !!v || 'Zip/Postal is required'
        : true
    },
    updateCountry: async function (v: string) {
      this.country = v;
      this.normalizeAddressRegion();

      await Vue.nextTick();

      (this.$refs.addressForm as any).validate();
    },
    goBack() {
      this.$store.commit('changeRegisterRenewMembershipInfo', { page: 1 });
    },
    returnHome() {
      this.$navigate('/');
    },
    next: function() {
      this.normalizeAddressRegion();
      const membership = this.$store.state.registerRenewMembershipInfo;

      const data = {
        ...membership.primary,
        country: this.country,
        address: this.address,
        international: this.international,
        city: this.city,
        stateProvince: this.stateProvince,
        zipPostal: this.zipPostal,
      };

      this.$store.commit('changeRegisterRenewMembershipInfo', {
        page: 3,
        subscribe: this.subscribe,
        firstClass: this.firstClass,
        primary: { ...data }
      })
    },
    pay: async function (data: AuthorizeNetResponseData) {
      this.normalizeAddressRegion();
      this.isProcessing = true;

      try {
        const response = await memberService.renewAussieTimes({
          country: this.country,
          address: this.address,
          international: this.international,
          city: this.city,
          stateProvince: this.stateProvince,
          zipPostal: this.zipPostal,
          firstClassSubscription: this.firstClass,
          fee: this.aussieTimesPrice + (this.firstClass ? this.firstClassPrice : this.standardPrice),
          paymentInfo: data
        });

        if (response.success) {
          const newUserInfo = await userService.getProfile();

          this.$store.commit('loadUserInfo', newUserInfo);

          this.isProcessing = false;
          this.showSuccess = true;
        } else {
          this.isProcessing = false;
          this.errorMessage = response.errors ? response.errors.join(',') : 'An Unknown Error Occured';
        }
      } catch {
        this.isProcessing = false;
        this.errorMessage = 'An Unknown Error Occured';
      }

      this.isProcessing = false;
    },
    normalizeAddressRegion: function () {
      const region = normalizeAddressRegion(
        this.country,
        this.stateProvince,
        this.international
      );
      this.stateProvince = region.state;
      this.international = region.international;
    },
  },
});
</script>
